import {
  Banner,
  Button,
  FormLayout,
  Layout,
  LegacyCard,
  Page,
  Select,
  SelectOption,
  TextField,
} from '@shopify/polaris'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { backendFetchResult, Result, SubscriptionContractSubscriptionStatus } from '../common/api'
import { currencyOptions } from '../common/currencies'
import { CustomerModal, CustomerResult } from '../common/CustomerModal'
import { DateTimePicker } from '../common/DateTimePicker'
import { Footer } from '../common/Footer'
import { resultErrorField, ResultToastOrBanner, useDialogActivator } from '../common/helpers'
import { SellingPlanInterval } from '../common/plan-models'
import { useShopCurrency } from '../common/Price'
import { ProductQuantityField } from '../common/ProductQuantityField'
import { ProductVariantModal, ProductVariantResult } from '../common/ProductVariantModal'
import { useSaveBarContext } from '../common/SaveBar'
import { IntervalCountField, IntervalField } from '../common/ScheduleFields'
import { useSessionStorage } from '../common/session-storage'
import { NextAction, nextActionOptions, SubscriptionContract } from '../common/subscription-models'
import { TabIndex, Tabs } from '../common/Tabs'
import * as urls from '../common/urls'
import { Address, CustomerAddress } from '../delivery-details-edit-dialog/models'
import { SaveBar } from '../main/SaveBarProvider'
import { UnpaidOrderWarningBanner } from '../subscription-page/NextActionDialog'
import {
  paymentMethodOptions as makePaymentMethodOptions,
  paymentMethodUnselected,
} from '../subscription-page/PaymentMethodDialog'

const windowWithCache = window as unknown as {
  paywhirlSubscriptionCreateCache?: {
    currencyCode: string
    variant: ProductVariantResult | null
    currentPrice: string
    quantity: number
    interval: SellingPlanInterval
    intervalCount: number
    nextBillingDate: string | null
    deliveryPrice: string
  }
}

const makeAddressOptions = (addresses: CustomerAddress[]): SelectOption[] =>
  addresses.map((adr: CustomerAddress, idx: number) => ({
    label: adr.formatted,
    value: `${idx}`,
  }))

const addressInputFromAddress = (address: CustomerAddress) => ({
  address1: address.address1,
  address2: address.address2,
  city: address.city,
  company: address.company,
  countryCode: address.countryCode,
  firstName: address.firstName,
  lastName: address.lastName,
  phone: address.phone,
  provinceCode: address.provinceCode,
  zip: address.zip,
})

interface SubscriptionContractCreateInput {
  status: SubscriptionContractSubscriptionStatus.Paused
  nextBillingDate: string
  paymentMethodId?: string
  billingPolicy: {
    interval: SellingPlanInterval
    intervalCount: number
  }
  deliveryPolicy: {
    interval: SellingPlanInterval
    intervalCount: number
  }
  deliveryPrice: string
  deliveryMethod: {
    shipping: {
      address?: Address
    }
  } | null
}

export const SubscriptionCreatePage = () => {
  const cache = windowWithCache.paywhirlSubscriptionCreateCache
  const { hideAndRedirect } = useSaveBarContext()

  const shopCurrency = useShopCurrency()
  const [currencyCode, setCurrencyCode] = useState<string>(cache?.currencyCode ?? shopCurrency)

  const [subscriptionCreateBannerDismissed, setSubscriptionCreateBannerDismissed] =
    useSessionStorage<boolean>('subscription-create-banner-dismissed', false)

  const [customer, setCustomer] = useState<CustomerResult | null>(null)
  const customerDialogActivator = useDialogActivator()

  const [variant, setVariant] = useState<ProductVariantResult | null>(cache?.variant ?? null)
  const variantDialogActivator = useDialogActivator()
  const [quantity, setQuantity] = useState<number>(cache?.quantity ?? 1)
  const [currentPrice, setCurrentPrice] = useState<string>(cache?.currentPrice ?? '0')

  const [paymentMethodOptions, setPaymentMethodOptions] = useState<SelectOption[]>(
    makePaymentMethodOptions([], { allowNone: true })
  )
  const [paymentMethodId, setPaymentMethodId] = useState<string>(paymentMethodUnselected)
  const [paymentMethodError, setPaymentMethodError] = useState<string | null>(null)

  const [nextAction, setNextAction] = useState<NextAction>('payment')

  const initialNextBillingDate = useMemo(() => {
    const date = new Date(cache?.nextBillingDate ?? Date.now() + 1000 * 60 * 60 * 24)
    date.setSeconds(0, 0)
    return date.toISOString()
  }, [cache?.nextBillingDate])

  const [nextBillingDate, setNextBillingDate] = useState<string>(initialNextBillingDate)
  const [intervalCount, setIntervalCount] = useState<number>(cache?.intervalCount ?? 1)
  const [interval, setInterval] = useState<SellingPlanInterval>(
    cache?.interval ?? SellingPlanInterval.MONTH
  )

  const [addressOptions, setAddressOptions] = useState<SelectOption[]>([])
  const [addressIndex, setAddressIndex] = useState<number>(0)
  const [deliveryPrice, setDeliveryPrice] = useState<string>(cache?.deliveryPrice ?? '0')

  const [saveResult, setSaveResult] = useState<Result<any> | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const cacheInputData = useCallback(() => {
    windowWithCache.paywhirlSubscriptionCreateCache = {
      currencyCode,
      nextBillingDate,
      interval,
      intervalCount,
      variant,
      currentPrice,
      quantity,
      deliveryPrice,
    }
  }, [
    currencyCode,
    nextBillingDate,
    interval,
    intervalCount,
    variant,
    currentPrice,
    quantity,
    deliveryPrice,
  ])

  const resetError = useCallback(
    (field: string) => {
      if (saveResult?.field === field) {
        setSaveResult(null)
      }
    },
    [setSaveResult, saveResult]
  )

  const onCustomerSelect = useCallback(
    (result: CustomerResult) => {
      setCustomer(result)
      setAddressOptions(makeAddressOptions(result.addresses))
      setAddressIndex(0)

      const paymentMethods = makePaymentMethodOptions(result.paymentMethods, { allowNone: true })
      const firstPaymentMethod = paymentMethods.find(
        (option) => option.value !== paymentMethodUnselected && option.value !== ''
      )

      setPaymentMethodOptions(paymentMethods)
      setPaymentMethodId(firstPaymentMethod?.value ?? paymentMethodUnselected)
      setPaymentMethodError(null)
      customerDialogActivator.close()
    },
    [customerDialogActivator]
  )

  const onVariantSelect = useCallback(
    (result: ProductVariantResult) => {
      setVariant(result)
      setCurrentPrice(result.price)

      if (!result.requiresShipping) {
        resetError('contract.deliveryMethod.shipping')
      }
      variantDialogActivator.close()
    },
    [variantDialogActivator, resetError]
  )

  const inputData = useMemo(() => {
    const address = customer?.addresses?.[addressIndex] ?? null
    const requiresShipping = variant?.requiresShipping ?? true

    const contract: SubscriptionContractCreateInput = {
      status: SubscriptionContractSubscriptionStatus.Paused,
      nextBillingDate,
      ...(paymentMethodId && paymentMethodId !== paymentMethodUnselected
        ? { paymentMethodId }
        : {}),
      billingPolicy: { interval, intervalCount },
      deliveryPolicy: { interval, intervalCount },
      deliveryPrice,
      deliveryMethod: requiresShipping
        ? {
            shipping: {
              address: address ? addressInputFromAddress(address) : undefined,
            },
          }
        : null,
    }

    const draftInput = {
      customerId: customer?.id ?? '',
      currencyCode,
      nextBillingDate,
      contract,
      lines: [
        {
          line: {
            productVariantId: variant?.id ?? '',
            currentPrice,
            quantity,
          },
        },
      ],
    }

    return { draftInput, nextAction }
  }, [
    currencyCode,
    customer,
    variant,
    currentPrice,
    quantity,
    paymentMethodId,
    nextAction,
    nextBillingDate,
    interval,
    intervalCount,
    addressIndex,
    deliveryPrice,
  ])

  const initialInputData = useRef(inputData)

  const isDirty = useMemo(() => {
    return JSON.stringify(initialInputData.current) !== JSON.stringify(inputData)
  }, [inputData])

  const onCreate = useCallback(async () => {
    if (!inputData) {
      return
    }

    if (paymentMethodId === paymentMethodUnselected) {
      setPaymentMethodError('Please select a payment method')
      return
    }

    const body = JSON.stringify(inputData)

    setIsSubmitting(true)
    const path = `/subscriptions`
    const res = await backendFetchResult<SubscriptionContract>('POST', path, { body })
    setIsSubmitting(false)

    setSaveResult(res)

    if (res.status === 'success' && res.data) {
      cacheInputData()
      hideAndRedirect(urls.subscriptionUrl(res.data!.gid), 'replace')
    }
  }, [inputData, paymentMethodId, cacheInputData, hideAndRedirect])

  const handleDiscard = useCallback(() => {
    hideAndRedirect(urls.subscriptionsUrl(), 'push')
  }, [hideAndRedirect])

  const customerFieldError = customer
    ? customer.addresses.length === 0
      ? "This customer doesn't have a valid address"
      : undefined
    : saveResult?.field === 'customerId'
    ? 'Please select a customer'
    : undefined

  useEffect(() => {
    const inlineErrorFields = [
      'customerId',
      'lines.0.line.productVariantId',
      'lines.0.line.currentPrice',
      'lines.0.line.quantity',
      'contract.paymentMethodId',
      'contract.deliveryMethod.shipping',
      'contract.deliveryPrice',
      'nextBillingDate',
    ]
    // Check if there is an error related to any known fields,
    // if not, set `field` to undefined so the error shows as a banner
    if (saveResult?.status === 'error' && !inlineErrorFields.includes(saveResult.field as string)) {
      setSaveResult({
        ...saveResult,
        field: undefined,
      })
    }
  }, [saveResult])

  return (
    <>
      <Tabs selected={TabIndex.Subscriptions} />
      <Page
        title="Create subscription"
        backAction={{ content: 'Subscriptions', url: urls.subscriptionsUrl() }}
      >
        <Layout>
          <Layout.Section>
            {!subscriptionCreateBannerDismissed && (
              <Banner tone="info" onDismiss={() => setSubscriptionCreateBannerDismissed(true)}>
                The new subscription will be created <b>inactive</b>, and won't be visible to the
                customer until it's activated
              </Banner>
            )}
            <ResultToastOrBanner
              result={saveResult}
              setResult={setSaveResult}
              bottomMargin={false}
            />
          </Layout.Section>
          <Layout.AnnotatedSection
            title="Basic settings"
            description="Note: basic settings cannot be edited later"
          >
            <LegacyCard sectioned>
              <FormLayout>
                <Select
                  label="Currency"
                  options={currencyOptions}
                  value={currencyCode}
                  onChange={setCurrencyCode}
                />
                <TextField
                  label="Customer"
                  readOnly={true}
                  value={customer?.displayName ?? ''}
                  connectedRight={
                    <Button onClick={customerDialogActivator.show} size="large">
                      Select
                    </Button>
                  }
                  autoComplete="off"
                  error={customerFieldError}
                />
              </FormLayout>
            </LegacyCard>
          </Layout.AnnotatedSection>

          <Layout.AnnotatedSection
            title="First item"
            description="You can add additional product(s) and customize the subscription further after it's drafted, in the next step."
          >
            <LegacyCard sectioned>
              <FormLayout>
                <TextField
                  label="Product variant"
                  readOnly={true}
                  value={variant?.title ?? ''}
                  connectedRight={
                    <Button onClick={variantDialogActivator.show} size="large">
                      Select
                    </Button>
                  }
                  autoComplete="off"
                  error={resultErrorField(saveResult, 'lines.0.line.productVariantId') ?? false}
                />

                <ProductQuantityField
                  value={quantity}
                  onChange={(quantity) => {
                    setQuantity(quantity)
                    resetError('lines.0.line.quantity')
                  }}
                  error={resultErrorField(saveResult, 'lines.0.line.quantity') ?? false}
                />

                <TextField
                  label="Price"
                  type="number"
                  min={0}
                  step={0.01}
                  prefix={currencyCode}
                  value={currentPrice}
                  onChange={(currentPrice) => {
                    setCurrentPrice(currentPrice)
                    resetError('lines.0.line.currentPrice')
                  }}
                  autoComplete="off"
                  error={resultErrorField(saveResult, 'lines.0.line.currentPrice') ?? false}
                />
              </FormLayout>
            </LegacyCard>
          </Layout.AnnotatedSection>

          <Layout.AnnotatedSection
            title="Payments"
            description="Select the payment method for the subscription. If the customer does not have a payment method on file, they will receive an email prompting them to add one when the subscription is activated."
          >
            <LegacyCard sectioned>
              <FormLayout>
                <Select
                  label="Payment method"
                  options={paymentMethodOptions}
                  value={paymentMethodId}
                  onChange={(paymentMethodId) => {
                    setPaymentMethodId(paymentMethodId)
                    setPaymentMethodError(null)
                    resetError('contract.paymentMethodId')
                  }}
                  error={
                    paymentMethodError ??
                    resultErrorField(saveResult, 'contract.paymentMethodId') ??
                    false
                  }
                />

                <Select
                  label="Next scheduled action"
                  options={nextActionOptions.filter(
                    (option) => option.value === 'payment' || option.value === 'unpaid-order'
                  )}
                  value={nextAction}
                  onChange={setNextAction as (val: string) => any}
                />

                {nextAction === 'payment' && customer && paymentMethodId === '' && (
                  <Banner tone="warning">
                    This subscription will be created without a payment method. It won't be able to
                    bill until one is added.
                  </Banner>
                )}

                {nextAction === 'unpaid-order' && <UnpaidOrderWarningBanner />}

                <DateTimePicker
                  label="First order"
                  initial={nextBillingDate}
                  onChange={(nextBillingDate) => {
                    setNextBillingDate(nextBillingDate)
                    resetError('nextBillingDate')
                  }}
                  error={resultErrorField(saveResult, 'nextBillingDate') ?? undefined}
                />

                <FormLayout.Group>
                  <IntervalCountField
                    label="Interval"
                    value={intervalCount}
                    onChange={setIntervalCount}
                  />
                  <IntervalField label="  " value={interval} onChange={setInterval} />
                </FormLayout.Group>
              </FormLayout>
            </LegacyCard>
          </Layout.AnnotatedSection>

          {variant?.requiresShipping !== false && (
            <Layout.AnnotatedSection
              title="Shipping"
              description="Select a shipping address and specify the price of shipping for subscription orders."
            >
              <LegacyCard sectioned>
                <FormLayout>
                  <Select
                    label="Address"
                    options={addressOptions}
                    value={addressIndex.toString()}
                    onChange={(val: string) => {
                      setAddressIndex(parseInt(val) || 0)
                      resetError('contract.deliveryMethod.shipping')
                    }}
                    error={
                      resultErrorField(saveResult, 'contract.deliveryMethod.shipping') ?? false
                    }
                  />
                  <TextField
                    label="Price"
                    type="number"
                    min={0}
                    step={0.01}
                    prefix={currencyCode}
                    value={deliveryPrice}
                    onChange={(val: string) => {
                      setDeliveryPrice(val)
                      resetError('contract.deliveryPrice')
                    }}
                    autoComplete="off"
                    error={resultErrorField(saveResult, 'contract.deliveryPrice') ?? false}
                  />
                </FormLayout>
              </LegacyCard>
            </Layout.AnnotatedSection>
          )}
          <Layout.Section>
            <Footer />
          </Layout.Section>
        </Layout>
        <CustomerModal
          activator={customerDialogActivator}
          onSelect={(result) => {
            onCustomerSelect(result)
            resetError('customerId')
          }}
        />
        <ProductVariantModal
          currency={currencyCode}
          open={variantDialogActivator.open}
          onClose={variantDialogActivator.close}
          onSelect={(result) => {
            onVariantSelect(result)
            resetError('lines.0.line.productVariantId')
          }}
        />
      </Page>
      <SaveBar
        isNew={true}
        isDirty={isDirty}
        onSave={onCreate}
        onDiscard={handleDiscard}
        isSubmitting={isSubmitting}
        isSaveDisabled={!isDirty}
      />
    </>
  )
}
